import React, { useEffect, useMemo, useState } from "react";
import Card from "components/card";

import {
  Activity,
  Factory,
  Gauge,
  Loader2,
  PackageCheck,
  ShieldCheck,
  Sparkles,
  Truck,
  Users,
  Wheat,
} from "lucide-react";

const DEFAULT_FORM = {
  rcnQuantityKg: "66138",
  tfrPercent: "10.51",
  outturnKOR: "43.43",
};

const FIELD_CONFIG = [
  {
    key: "rcnQuantityKg",
    label: "RCN Input (kg)",
    min: 0,
    step: "0.01",
  },
  {
    key: "tfrPercent",
    label: "TDR (%)",
    min: 0,
    max: 100,
    step: "0.01",
  },
  {
    key: "outturnKOR",
    label: "Outturn",
    min: 0,
    step: "0.01",
  },
];

function formatValue(value, digits = 2) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function parseInput(value) {
  const normalized = String(value).trim();

  if (!normalized) {
    return { number: null, empty: true };
  }

  const parsed = Number(normalized);

  return {
    number: Number.isFinite(parsed) ? parsed : null,
    empty: false,
  };
}

function validateForm(formState) {
  const nextErrors = {};
  const parsedValues = {};

  FIELD_CONFIG.forEach((field) => {
    const { number, empty } = parseInput(formState[field.key]);

    if (empty) {
      nextErrors[field.key] = "This field is required.";
      return;
    }

    if (number === null) {
      nextErrors[field.key] = "Enter valid number.";
      return;
    }

    parsedValues[field.key] = number;
  });

  return {
    parsedValues,
    errors: nextErrors,
    isValid: Object.keys(nextErrors).length === 0,
  };
}

function lookupKORRules(kor) {
  if (kor < 44) return { animalFeedPercent: 7.0 };
  if (kor >= 44 && kor < 45) return { animalFeedPercent: 5.0 };
  if (kor >= 45 && kor < 46) return { animalFeedPercent: 4.0 };
  if (kor >= 46 && kor < 47) return { animalFeedPercent: 3.5 };
  return { animalFeedPercent: 3.0 };
}

function executePipeline(rcnQuantityKg, tfrPercent, outturnKOR) {
  const korRules = lookupKORRules(outturnKOR);
  const tfr = tfrPercent / 100;
  const excelWorkersCount = 150;
  const excelBkBoxesCount = 150;

  // ==================================================
  // KERNEL EXPECTATION
  // Excel B8 = B5*(B6/0.176/1000)*(1-C6) = rcnQty*(kor/176)*(1-tfr)
  // ==================================================
  const kernelExpectationKg = rcnQuantityKg * (outturnKOR / 176) * (1 - tfr);

  // ==================================================
  // SHELLING
  // Excel G11=0.95 | I11=B8*0.95 | J11=B8-I11 | C13=B8
  // ==================================================
  const shellingWholesKg = kernelExpectationKg * 0.95;   // I11
  const shellingPiecesKg = kernelExpectationKg - shellingWholesKg; // J11
  const shellingExitKg = kernelExpectationKg;             // C13
  const shellingWholesPercent = shellingExitKg
    ? (shellingWholesKg / shellingExitKg) * 100
    : 0;
  const shellingPiecesPercent = 100 - shellingWholesPercent;

  // ==================================================
  // DRYING (two-pass reduction)
  // Excel B16=C11*0.95, B17=C12*0.95 → first moisture pass (B column)
  // C16=B16*0.95, C17=B17+(B16-C16) → second pass (C column)
  // C18=B16+B17 → drying exit = shellingExit * 0.95 (5% moisture loss)
  // ==================================================
  const dryingBWholesKg = shellingWholesKg * 0.95;                           // B16
  const dryingBPiecesKg = shellingPiecesKg * 0.95;                           // B17
  const dryingExitKg    = dryingBWholesKg + dryingBPiecesKg;                 // C18 = shellingExit * 0.95
  const dryingWholesKg  = dryingBWholesKg * 0.95;                            // C16 = shellingWholes * 0.9025
  const dryingPiecesKg  = dryingBPiecesKg + (dryingBWholesKg - dryingWholesKg); // C17
  const dryingWholesPercent = dryingExitKg
    ? (dryingWholesKg / dryingExitKg) * 100
    : 0;
  const dryingPiecesPercent = 100 - dryingWholesPercent;

  // ==================================================
  // MECHANICAL PEELING
  // Excel G21=0.10 (testa) | B21=0.05 (mech breakage) | H33=0.05 (grading breakage)
  // H21=C16*(1-G21) | I21=C17*(1-G21) | J21=G21*C18
  // C21=(H21*(1-B21))*(1-H33)
  // C22=(I21+(H21-C21))*(1-H33)  ← total breakage, not just mechanical
  // C23=(C21+C22)*(1-H33)        ← final grading pass applied to sum
  // ==================================================
  const testaRate          = 0.10;
  const mechanicalBreakage = 0.05;
  const gradingBreakageRate = 0.05; // H33

  const testaWholesKg  = dryingWholesKg * (1 - testaRate);  // H21
  const testaPiecesKg  = dryingPiecesKg * (1 - testaRate);  // I21
  const testaRemovedKg = dryingExitKg * testaRate;           // J21

  const peelingWholesKg = (testaWholesKg * (1 - mechanicalBreakage)) * (1 - gradingBreakageRate); // C21
  const peelingPiecesKg = (testaPiecesKg + (testaWholesKg - peelingWholesKg)) * (1 - gradingBreakageRate); // C22
  const peelingExitKg   = (peelingWholesKg + peelingPiecesKg) * (1 - gradingBreakageRate); // C23

  // ==================================================
  // MANUAL PEELING
  // Excel C25=C21*0.8 | C26=C21*0.2 | C28=C26*0.7 | C29=C26*0.3
  // ==================================================
  const manualPeeledWholesKg = peelingWholesKg * 0.8; // C25
  const unpeeledRecycledKg   = peelingWholesKg * 0.2; // C26
  const forManualPeelingKg   = unpeeledRecycledKg * 0.7; // C28
  const hardTestaKg          = unpeeledRecycledKg * 0.3; // C29

  // ==================================================
  // LABOUR (Total manual input / productivity target)
  // ==================================================
  const workerProductivityTarget = 12.0;
  const necessaryLaborForce = Math.ceil(
    (forManualPeelingKg + hardTestaKg) / workerProductivityTarget
  );
  const manualPeelingProductivity = (forManualPeelingKg + hardTestaKg) / excelWorkersCount;

  // ==================================================
  // GRADING
  // Excel C38=C25+C28 | C33=C38*0.8 | J33=(C21+C22)-C23 (grading loss)
  // B42=C23-C38 (pieces for packing)
  // ==================================================
  const soundGradedKernelsKg     = manualPeeledWholesKg + forManualPeelingKg; // C38
  const animalFeedWeightKg       = (peelingWholesKg + peelingPiecesKg) - peelingExitKg; // J33 = grading loss
  const exportPremiumWhiteWholesKg = soundGradedKernelsKg * 0.8; // C33
  const lowerGradesAndBrokensKg  = peelingExitKg - soundGradedKernelsKg;      // B42

  // ==================================================
  // PACKING
  // Excel B43=C23 | C43=(B41+C28)/22.68 + B42/22.68 = (peelingExit+forManualPeeling)/22.68
  // Recovery = C23/B5
  // ==================================================
  const netPackedWeightKg = peelingExitKg; // B43 = C23
  const shippingCartonsCount = (soundGradedKernelsKg + forManualPeelingKg + lowerGradesAndBrokensKg) / 22.68; // C43
  const physicalRecoveryYieldPercent = (netPackedWeightKg / rcnQuantityKg) * 100;

  // User-requested packing logic: add For Manual Peeling on top of wholes total.
  const packagingWholesKg = soundGradedKernelsKg + forManualPeelingKg;
  const packagingWholesCartons = packagingWholesKg / 22.68;
  const packagingPiecesKg = lowerGradesAndBrokensKg;
  const packagingPiecesCartons = packagingPiecesKg / 22.68;
  const packagingTotalKg = packagingWholesKg + packagingPiecesKg;
  const packagingTotalCartons = packagingWholesCartons + packagingPiecesCartons;
  const packagingWholesPercent = packagingTotalCartons
    ? (packagingWholesCartons / packagingTotalCartons) * 100
    : 0;
  const packagingPiecesPercent = 100 - packagingWholesPercent;

  const gradingWhiteWholesKg = exportPremiumWhiteWholesKg; // C33
  const gradingWhiteWholesPercent = 80;
  const gradingW3mPercent = 80;
  const gradingTotalWholesKg = soundGradedKernelsKg; // C38
  const gradingTotalWholesCartons = gradingTotalWholesKg / 22.68; // B38

  const mechanicalWholesPercent = peelingExitKg
    ? (peelingWholesKg / peelingExitKg) * 100
    : 0;
  const mechanicalPiecesPercent = 100 - mechanicalWholesPercent;

  return {
    korRules,
    data: {
      kernelExpectationKg,
      shellingWholesKg,
      shellingPiecesKg,
      shellingExitKg,
      shellingWholesPercent,
      shellingPiecesPercent,
      dryingBWholesKg,
      dryingBPiecesKg,
      dryingWholesKg,
      dryingPiecesKg,
      dryingExitKg,
      dryingWholesPercent,
      dryingPiecesPercent,
      peelingWholesKg,
      peelingPiecesKg,
      peelingExitKg,
      testaRemovedKg,
      manualPeeledWholesKg,
      unpeeledRecycledKg,
      forManualPeelingKg,
      hardTestaKg,
      necessaryLaborForce,
      manualPeelingProductivity,
      soundGradedKernelsKg,
      animalFeedWeightKg,
      exportPremiumWhiteWholesKg,
      lowerGradesAndBrokensKg,
      netPackedWeightKg,
      shippingCartonsCount,
      physicalRecoveryYieldPercent,
      excelWorkersCount,
      excelBkBoxesCount,
      packagingWholesKg,
      packagingWholesCartons,
      packagingWholesPercent,
      packagingPiecesKg,
      packagingPiecesCartons,
      packagingPiecesPercent,
      packagingTotalKg,
      packagingTotalCartons,
      gradingWhiteWholesKg,
      gradingWhiteWholesPercent,
      gradingW3mPercent,
      gradingTotalWholesKg,
      gradingTotalWholesCartons,
      mechanicalWholesPercent,
      mechanicalPiecesPercent,
    },
  };
}

function MetricRow({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-gray-100 bg-white/80 px-3 py-2.5 dark:bg-navy-900 dark:border-navy-700">
      <span className="text-sm text-gray-600 dark:text-gray-400">
        {label}
      </span>
      <span className="text-sm font-bold text-navy-700 dark:text-white">
        {value}
      </span>
    </div>
  );
}

function StageCard({ title, description, icon: Icon, metrics }) {
  return (
    <Card extra="rounded-[20px] border border-gray-100 p-6 shadow-lg shadow-gray-200/60 bg-white dark:bg-navy-800 dark:border-none">
      <div className="mb-5 flex items-start gap-4">
        <div className="rounded-xl bg-lightPrimary p-3 dark:bg-navy-700">
          <Icon className="h-5 w-5 text-brand-500" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-navy-700 dark:text-white">
            {title}
          </h3>
          {description ? (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {description}
            </p>
          ) : null}
        </div>
      </div>
      <div className="space-y-2">
        {metrics.map((metric) => (
          <MetricRow
            key={metric.label}
            label={metric.label}
            value={metric.value}
          />
        ))}
      </div>
    </Card>
  );
}

function KpiCard({ title, value, sub, icon: Icon, iconClass }) {
  return (
    <Card extra="rounded-[20px] border border-gray-100 p-5 shadow-lg shadow-gray-200/60 bg-white dark:bg-navy-800 dark:border-none">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <p className="mt-2 text-2xl font-bold text-navy-700 dark:text-white">{value}</p>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{sub}</p>
        </div>
        <div className={`rounded-xl p-3 ${iconClass}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

export default function RCNCalculator() {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);

  const validation = useMemo(() => validateForm(form), [form]);

  const [result, setResult] = useState(() => {
    const initial = validateForm(DEFAULT_FORM);
    return executePipeline(
      initial.parsedValues.rcnQuantityKg,
      initial.parsedValues.tfrPercent,
      initial.parsedValues.outturnKOR
    );
  });

  useEffect(() => {
    setErrors(validation.errors);

    if (validation.isValid) {
      setResult(
        executePipeline(
          validation.parsedValues.rcnQuantityKg,
          validation.parsedValues.tfrPercent,
          validation.parsedValues.outturnKOR
        )
      );
    }
  }, [validation]);

  const handleInputChange = (key) => (event) => {
    setForm((current) => ({
      ...current,
      [key]: event.target.value,
    }));
  };

  const handleCalculate = async (e) => {
    e.preventDefault();

    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsCalculating(true);
    await new Promise((resolve) => setTimeout(resolve, 400));

    setResult(
      executePipeline(
        validation.parsedValues.rcnQuantityKg,
        validation.parsedValues.tfrPercent,
        validation.parsedValues.outturnKOR
      )
    );
    setIsCalculating(false);
  };

  const { data } = result;

  const topKpis = [
    {
      title: "Packing Total",
      value: `${formatValue(data.packagingTotalKg, 0)} kg`,
      sub: "Total packing output",
      icon: PackageCheck,
      iconClass: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
    },
    {
      title: "Total Cartons",
      value: `${formatValue(data.packagingTotalCartons, 0)} cartons`,
      sub: "Final cartons from packing",
      icon: Truck,
      iconClass: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
    },
    {
      title: "Mechanical Exit",
      value: `${formatValue(data.peelingExitKg, 0)} kg`,
      sub: `${formatValue(data.mechanicalWholesPercent, 1)}% wholes / ${formatValue(data.mechanicalPiecesPercent, 1)}% pieces`,
      icon: Activity,
      iconClass: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
    },
    {
      title: "White Wholes",
      value: `${formatValue(data.gradingWhiteWholesKg, 0)} kg`,
      sub: "Grading white wholes at 80%",
      icon: Wheat,
      iconClass: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
    },
    {
      title: "Manual Productivity",
      value: `${formatValue(data.manualPeelingProductivity, 1)}`,
      sub: `${data.excelWorkersCount} workers / ${data.excelBkBoxesCount} BK-Boxes`,
      icon: Users,
      iconClass: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
    },
  ];

  const stages = [
    {
      title: "Shelling",
      // description: "Shelling totals and percentage split as in Excel.",
      icon: Wheat,
      metrics: [
        { label: "Wholes", value: `${formatValue(data.shellingWholesKg, 0)} kg | ${formatValue(data.shellingWholesPercent, 1)}%` },
        { label: "Pieces", value: `${formatValue(data.shellingPiecesKg, 0)} kg | ${formatValue(data.shellingPiecesPercent, 1)}%` },
        { label: "Exit", value: `${formatValue(data.shellingExitKg, 0)} kg` },
        { label: "Shelling Wholes Rate", value: `${formatValue(data.shellingWholesPercent, 2)}%` },
      ],
    },
    {
      title: "Drying",
      // description: "Drying input/output values and split percentages.",
      icon: Factory,
      metrics: [
        { label: "Wholes", value: `${formatValue(data.dryingBWholesKg, 2)} -> ${formatValue(data.dryingWholesKg, 0)} kg | ${formatValue(data.dryingWholesPercent, 1)}%` },
        { label: "Pieces", value: `${formatValue(data.dryingBPiecesKg, 2)} -> ${formatValue(data.dryingPiecesKg, 0)} kg | ${formatValue(data.dryingPiecesPercent, 1)}%` },
        { label: "Exit", value: `${formatValue(data.dryingExitKg, 0)} kg` },
      ],
    },
    {
      title: "Mechanical Peeling",
      // description: "Breakage and testa outputs aligned with Excel structure.",
      icon: ShieldCheck,
      metrics: [
        { label: "Wholes (Breakage 5%)", value: `${formatValue(data.peelingWholesKg, 0)} kg | ${formatValue(data.mechanicalWholesPercent, 1)}%` },
        { label: "Pieces", value: `${formatValue(data.peelingPiecesKg, 0)} kg | ${formatValue(data.mechanicalPiecesPercent, 1)}%` },
        { label: "Exit", value: `${formatValue(data.peelingExitKg, 0)} kg` },
        { label: "Testa Rate / TESTA", value: `10% | ${formatValue(data.testaRemovedKg, 2)} kg` },
        { label: "Peeled Wholes (80%)", value: `${formatValue(data.manualPeeledWholesKg, 0)} kg` },
        { label: "Unpeed Wholes (20%)", value: `${formatValue(data.unpeeledRecycledKg, 0)} kg` },
        { label: "For Manual Peeling (70%)", value: `${formatValue(data.forManualPeelingKg, 0)} kg` },
        { label: "Hard Testa (30%)", value: `${formatValue(data.hardTestaKg, 0)} kg` },
        { label: "Nº Of workers / BK-BOXES", value: `${data.excelWorkersCount} / ${data.excelBkBoxesCount}` },
        { label: "Man. Peeling Productivity", value: `${formatValue(data.manualPeelingProductivity, 1)}` },
      ],
    },
    {
      title: "Grading",
      // description: "Grading lines as shown in the Excel sheet.",
      icon: Sparkles,
      metrics: [
        { label: "White Wholes (ww/w1)", value: `${formatValue(data.gradingWhiteWholesKg, 2)} kg | ${formatValue(data.gradingWhiteWholesPercent, 0)}%` },
        { label: "Scotch Wholes (sw/w2)", value: `-` },
        { label: "Desert Wholes (DwL/DwD)", value: `-` },
        { label: "W3m", value: `${formatValue(data.gradingW3mPercent, 0)}%` },
        { label: "sk1/SK2 Wholes", value: `-` },
        { label: "Total Wholes", value: `${formatValue(data.gradingTotalWholesCartons, 0)} cartons | ${formatValue(data.gradingTotalWholesKg, 0)} kg` },
      ],
    },
    {
      title: "Packing",
      // description: "Packing with cartons and percentage split only.",
      icon: PackageCheck,
      metrics: [
        { label: "Wholes (incl. For Manual Peeling 70%)", value: `${formatValue(data.packagingWholesKg, 0)} kg | ${formatValue(data.packagingWholesCartons, 0)} cartons | ${formatValue(data.packagingWholesPercent, 1)}%` },
        { label: "Pieces", value: `${formatValue(data.packagingPiecesKg, 0)} kg | ${formatValue(data.packagingPiecesCartons, 0)} cartons | ${formatValue(data.packagingPiecesPercent, 1)}%` },
        { label: "Total", value: `${formatValue(data.packagingTotalKg, 0)} kg | ${formatValue(data.packagingTotalCartons, 0)} cartons` },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-slate-50 to-green-50 px-4 py-8 dark:from-navy-900 dark:via-navy-900 dark:to-navy-900">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 overflow-hidden rounded-[24px] border border-white bg-white/80 p-6 shadow-xl shadow-gray-200/70 backdrop-blur dark:bg-navy-800 dark:border-none dark:shadow-none">
          <div className="flex flex-col items-center justify-between gap-4 text-center lg:flex-row lg:text-left">
            <div>
              <img
                src="/newlogo-removebg-preview.png"
                alt="USIBRAS Ghana Limited Logo"
                className="mb-4 h-14 w-auto object-contain"
              />
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-lightPrimary px-4 py-2 text-sm font-semibold text-brand-500 dark:bg-navy-700">
                <Sparkles className="h-4 w-4" />
                Factory Intelligence Console
              </div>
              <h1 className="text-3xl font-extrabold text-navy-700 lg:text-4xl dark:text-white">
                Industrial Cashew Processing Simulator
              </h1>
              {/* <p className="mt-3 max-w-3xl text-base text-gray-600 dark:text-gray-400">
                Replace Excel guesswork with live, stage-by-stage production intelligence. Adjust RCN, TFR, and KOR to instantly see operational impact.
              </p> */}
            </div>

            <div className="grid w-full max-w-sm grid-cols-2 gap-3 rounded-2xl bg-navy-700 p-4 text-left text-white dark:bg-navy-900">
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-300">Engine</p>
                <p className="mt-1 font-bold">Real-time</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-300">Pipeline</p>
                <p className="mt-1 font-bold">5 Stages</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-300">Rules</p>
                <p className="mt-1 font-bold">KOR-driven</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-gray-300">Mode</p>
                <p className="mt-1 font-bold">Operator-ready</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
          {topKpis.map((kpi) => (
            <KpiCard key={kpi.title} {...kpi} />
          ))}
        </div>

        <div className="grid gap-6 xl:grid-cols-[360px_minmax(0,1fr)]">
          <aside>
            <Card extra="sticky top-6 rounded-[20px] border border-gray-100 p-6 shadow-xl bg-white dark:bg-navy-800 dark:border-none">
              <div className="mb-6 flex items-center gap-3">
                <div className="rounded-xl bg-lightPrimary p-3 dark:bg-navy-700">
                  <Gauge className="h-5 w-5 text-brand-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-navy-700 dark:text-white">
                    Simulation Inputs
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Update fields to run the production engine.
                  </p>
                </div>
              </div>

              <form className="space-y-5" onSubmit={handleCalculate}>
                {FIELD_CONFIG.map((field) => (
                  <div key={field.key}>
                    <label className="mb-2 block text-sm font-bold text-navy-700 dark:text-white">
                      {field.label}
                    </label>
                    <input
                      type="number"
                      min={field.min}
                      max={field.max}
                      step={field.step}
                      value={form[field.key]}
                      onChange={handleInputChange(field.key)}
                      className="w-full rounded-xl border border-gray-300 bg-gray-50 px-3 py-3 outline-none transition focus:border-blue-500 focus:bg-white dark:bg-navy-900 dark:border-navy-700 dark:text-white dark:focus:bg-navy-900"
                    />
                    {errors[field.key] && (
                      <p className="mt-1 text-xs text-red-500">
                        {errors[field.key]}
                      </p>
                    )}
                  </div>
                ))}

                <button
                  type="submit"
                  disabled={isCalculating}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-500 font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {isCalculating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Gauge className="h-4 w-4" />
                  )}
                  {isCalculating ? "Calculating..." : "Run Simulation"}
                </button>

                <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-xs text-blue-700 dark:bg-navy-900/50 dark:border-navy-700 dark:text-blue-400">
                  Tip: This model recalculates instantly as values change, so operators can compare scenarios quickly before making production decisions.
                </div>
              </form>
            </Card>
          </aside>

          <main>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              {stages.map((stage) => (
                <StageCard key={stage.title} {...stage} />
              ))}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}